/*
 * What the pricing page sells, in two ways to buy: Team, by the user, on a
 * shared deployment; and Core, Scale and Enterprise, each on a server of its
 * own. The plan comparison (FEATURE_ROWS) follows the Mercury Labs
 * Operations Twin, channel `commercials`, Data Set "Plan Features", View
 * `plan-comparison` (read 2026-10-01), which holds the record and each row's
 * state. Team, and Core's extra users, are proposals there: this page is
 * their draft, not yet public. One row runs ahead of the record: the tools
 * row names every tool the landing shows (Archie, 2026-10-01), where the
 * record names Claude and ChatGPT.
 */

/** The plans on a server of their own. */
export type TwinPlanId = "core" | "scale" | "enterprise";
/** Every plan the page sells. */
export type PlanId = "team" | TwinPlanId;

type Plan<Id extends PlanId> = {
  id: Id;
  name: string;
  eyebrow: string;
  price: string;
  cadence?: string;
  monthlyPrice: number;
  description: string;
  detail: string;
};

export type TwinPlan = Plan<TwinPlanId>;
export type AnyPlan = Plan<PlanId>;

export type CreditPack = {
  id: string;
  name: string;
  shortName: string;
  price: string;
  monthlyPrice: number;
  credits: number;
  description: string;
};

function gbp(amount: number): string {
  return `£${amount.toLocaleString("en-GB")}`;
}

/** Team's price for each user, each month: the per-user plan, on a shared deployment. */
export const TEAM_SEAT_PRICE = 75;
/** The users Core's price covers. */
export const CORE_INCLUDED_USERS = 10;
/** What Core charges for each user beyond those, each month. */
export const CORE_EXTRA_USER_PRICE = 75;

/**
 * How a Team customer starts, in Archie's words (2026-10-01): Team's card
 * label, and the package bar's action while Team is chosen.
 */
export const TEAM_START = "Get started today";

/** Team: its price is for one user, so `monthlyPrice` is too. */
export const TEAM_PLAN = {
  id: "team",
  name: "Team",
  eyebrow: TEAM_START,
  price: gbp(TEAM_SEAT_PRICE),
  cadence: "/ user / month",
  monthlyPrice: TEAM_SEAT_PRICE,
  description: "Your Twin for a team that works in cloud apps, on a shared deployment.",
  detail: "Self-serve, with standard product support and optional engineering days.",
} satisfies Plan<"team">;

export const TWIN_PLANS = [
  {
    id: "core",
    name: "Core",
    eyebrow: "Start here",
    price: "From £2,000",
    cadence: "/ month",
    monthlyPrice: 2000,
    description:
      "Your Twin for one focused team, connecting the records and business rules behind its work.",
    detail: "Managed deployment, standard onboarding and product support.",
  },
  {
    id: "scale",
    name: "Scale",
    eyebrow: "Grow adoption",
    price: "From £5,000",
    cadence: "/ month",
    monthlyPrice: 5000,
    description:
      "Bring your Twin to more teams, workflows and systems, with support as you expand.",
    detail: "Pricing reflects your teams, systems and support needs.",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    eyebrow: "Larger organisations",
    price: "From £15,000",
    cadence: "/ month",
    monthlyPrice: 15000,
    description:
      "Your Twin for complex organisations that need tailored controls, hosting and support.",
    detail: "We agree the infrastructure, access rules and support with you.",
  },
] satisfies [TwinPlan, TwinPlan, TwinPlan];

/** Every plan, in the order the page sets them: per user, then the dedicated plans. */
export const PLANS: readonly AnyPlan[] = [TEAM_PLAN, ...TWIN_PLANS];

export const CREDIT_PACKS = [
  {
    id: "credits-none",
    name: "No credit pack",
    shortName: "None",
    price: "£0",
    monthlyPrice: 0,
    credits: 0,
    description: "Add model capacity when your first workflow is ready.",
  },
  {
    id: "credits-starter",
    name: "Starter credits",
    shortName: "Starter",
    price: "£250 / month",
    monthlyPrice: 250,
    credits: 25_000,
    description:
      "25,000 credits for one focused workflow or a small group of regular users.",
  },
  {
    id: "credits-team",
    name: "Team credits",
    shortName: "Team",
    price: "£750 / month",
    monthlyPrice: 750,
    credits: 100_000,
    description:
      "100,000 credits for several approved workflows and a wider group of users.",
  },
  {
    id: "credits-scale",
    name: "Scale credits",
    shortName: "Scale",
    price: "£1,800 / month",
    monthlyPrice: 1800,
    credits: 300_000,
    description:
      "300,000 credits for high-volume operations or committed model usage.",
  },
] satisfies [CreditPack, CreditPack, CreditPack, CreditPack];

export const FDE_DAY_RATE = 1200;
export const MAX_FDE_DAYS = 20;

/**
 * Forward-deployed engineering, in the words the pricing page's slider and
 * the home's engineering band share: what the time is for, and how our
 * engineers help.
 */
export const ENGINEERING_OFFER = {
  description: "Reserve engineering time to connect your systems, check a first workflow or extend your Twin. We agree the work with you.",
  benefits: [
    { title: "Work with our engineers", detail: "Work directly with engineers on your systems, business questions and the results you need." },
    { title: "Faster path to live use", detail: "Connect the systems for a useful workflow and check the results with your team." },
    { title: "Expand where it helps", detail: "Agree additional connections and workflow improvements as your needs change." },
  ],
  /** What the day rate does not cover. */
  beyond: "Larger or dedicated teams are agreed separately.",
} as const;

/** The pricing page's engineering days, as a place other pages link to. */
export const ENGINEERING_ANCHOR = "engineering";

function published<T>(item: T | undefined, what: string): T {
  if (!item) throw new Error(`The pricing data no longer has ${what}.`);
  return item;
}

/** The smallest pack that costs anything: what the other pages quote AI usage from, and what a Team top-up adds. */
export const SMALLEST_PAID_PACK = published(
  CREDIT_PACKS.find((pack) => pack.monthlyPrice > 0),
  "a paid credit pack",
);

/** The starting prices other pages quote (the home's hero and start band, the solution pages), from the data above. */
export const STARTING_PRICES = {
  team: gbp(TEAM_SEAT_PRICE),
  core: gbp(published(TWIN_PLANS.find((plan) => plan.id === "core"), "the Core plan").monthlyPrice),
  credits: gbp(SMALLEST_PAID_PACK.monthlyPrice),
  engineeringDay: gbp(FDE_DAY_RATE),
} as const;

/** One row of the plan comparison: a feature, and what each plan says of it. */
export type FeatureRow = { group: string; feature: string } & Record<PlanId, string>;

/**
 * The plan comparison, a group at a time. "Included" draws as a tick, a
 * value starting "Not included" as a dash (with what the plan has instead
 * after a colon), and anything else as its own words.
 */
export const FEATURE_ROWS: readonly FeatureRow[] = [
  {
    group: "Twin foundation",
    feature: "A dedicated server for your organisation",
    team: "Not included: shared deployment",
    core: "EU or US region",
    scale: "EU or US region",
    enterprise: "EU or US, tailored environment",
  },
  {
    group: "Twin foundation",
    feature: "Answers from the checked Twin, with sources and limits",
    team: "Included",
    core: "Included",
    scale: "Included",
    enterprise: "Included",
  },
  {
    group: "Twin foundation",
    feature: "Single sign-on and tailored access rules",
    team: "Not included",
    core: "Included",
    scale: "Included",
    enterprise: "Tailored controls",
  },
  {
    group: "Business information",
    feature: "Approved source connectors, starting read-only",
    team: "Cloud app connectors only",
    core: "Initial source set",
    scale: "More connected systems",
    enterprise: "Agreed system coverage",
  },
  {
    group: "Business information",
    feature: "Twin data refresh",
    team: "Daily scheduled refresh",
    core: "Daily scheduled refresh",
    scale: "Higher-frequency refresh",
    enterprise: "Refresh agreed for each source",
  },
  {
    group: "Business information",
    feature: "Historical source import",
    team: "Up to 12 months per source",
    core: "Up to 12 months per source",
    scale: "Extended history",
    enterprise: "History agreed for each source",
  },
  {
    group: "Business information",
    feature: "Business definitions with review and version history",
    team: "Included",
    core: "Included",
    scale: "Shared across teams",
    enterprise: "Tailored controls",
  },
  {
    group: "Connectivity",
    feature: "Database plugins",
    team: "Not included",
    core: "Included",
    scale: "Included",
    enterprise: "Included",
  },
  {
    group: "Connectivity",
    feature: "Fixed outbound address for ERP and on-premise systems",
    team: "Not included",
    core: "Included",
    scale: "Included",
    enterprise: "Included",
  },
  {
    group: "Connectivity",
    feature: "Private network access",
    team: "Not included",
    core: "Included",
    scale: "Included",
    enterprise: "Included",
  },
  {
    group: "Adoption",
    feature: "Team access",
    team: `${gbp(TEAM_SEAT_PRICE)} per user`,
    core: `Up to ${CORE_INCLUDED_USERS} users`,
    scale: "Unlimited users",
    enterprise: "Unlimited users",
  },
  {
    group: "Adoption",
    feature: `Extra users beyond the included ${CORE_INCLUDED_USERS}`,
    team: "Per user from the first",
    core: `${gbp(CORE_EXTRA_USER_PRICE)} per extra user`,
    scale: "Unlimited users",
    enterprise: "Unlimited users",
  },
  {
    group: "Adoption",
    feature: "Configured models for approved workflows",
    team: "Usage allowance included, top-ups available",
    core: "Credit packs",
    scale: "Credit packs",
    enterprise: "Custom model routes",
  },
  {
    group: "Adoption",
    feature: "Custom model routes",
    team: "Not included",
    core: "Not included",
    scale: "Not included",
    enterprise: "Included",
  },
  {
    group: "Adoption",
    feature: "Browser and terminal capabilities",
    team: "Not included",
    core: "Opt-in",
    scale: "Opt-in",
    enterprise: "Opt-in",
  },
  {
    group: "Adoption",
    feature: "Third-party AI and developer access",
    team: "Configured access to your Twin; an organisation API key counts as a user",
    core: "Configured access to your Twin",
    scale: "Configured access to your Twin",
    enterprise: "Configured access to your Twin",
  },
  {
    group: "Adoption",
    feature: "Use your Twin from ChatGPT, Claude, Gemini, Microsoft 365 Copilot and Teams",
    team: "Included",
    core: "Included",
    scale: "Included",
    enterprise: "Included",
  },
  {
    group: "Operations",
    feature: "AI usage and provider costs, where available",
    team: "Included",
    core: "Included",
    scale: "Included",
    enterprise: "Tailored reporting",
  },
  {
    group: "Operations",
    feature: "Product support",
    team: "Self-serve, standard",
    core: "Standard",
    scale: "Dedicated support channel",
    enterprise: "Dedicated channel and named service lead",
  },
  {
    group: "Delivery",
    feature: "Optional engineering support",
    team: "Optional",
    core: "Optional",
    scale: "Optional",
    enterprise: "Agreed for your programme",
  },
];
